/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { ApplicationRef, Component, ComponentFactory, ComponentFactoryResolver, Inject, Injector, Input, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { HotToastService } from '@ngneat/hot-toast';
import { ZuperDialogService } from '@zuper/components/zuper-dialog/zuper-dialog.service';
import { FilePlaceholderOptions } from 'app/components/file-placeholder/file-placeholder.interface';
import { GalleryDetailsComponent } from 'app/components/gallery-details/gallery-details.component';
import { moduleKey } from 'app/core/constants/constants';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { DownloadFileLinkService } from 'app/core/services/download_file_link/download_file_link.service';
import { NotesService } from 'app/core/services/notes/notes.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { ModuleIcon } from 'app/interfaces/module-icon.interface';
import dayjs from 'dayjs';
import { AfterAppendSubHtmlDetail } from 'lightgallery/lg-events';
import { LightGallery } from 'lightgallery/lightgallery';
import lgVideo from 'lightgallery/plugins/video';
import _ from 'lodash';
import { NoteVisibilityType, ParamKey } from '../notes.interface';
import { redirectUrl } from '../../../components/datagrid/datagrid.interface';
import { environment } from 'environments/environment'; 

@Component({
  selector: 'app-note-details',
  templateUrl: './note-details.component.html',
  styleUrls: ['./note-details.component.scss']
})
export class NoteDetailsComponent implements OnInit {
  noteUid: string;
  currentModule: string;
  showEditIcon: boolean;
  note: any;
  user: any = this.utilityService.user;
  lightGallery: LightGallery;
  allImages: Array<any> = [];
  allImageMap: any = {};
  settings = {
      counter: true,
      dynamic: true,
      licenseKey: environment.lightGalleryKey,
      dynamicEl: this.allImages,
      loop: false,
      videojs: true,
      hideControlOnEnd: true,
      slideEndAnimation: false,
      appendSubHtmlTo: '.lg-outer',
      download: false,
      plugins: [lgVideo]
  };
  months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  allModules = ['JOB','CUSTOMER','ORGANIZATION','PROPERTY','ESTIMATES','INVOICE','SERVICE_CONTRACTS','ASSETS', 'PROJECT'];
  paramKey = ParamKey;
  redirectUrl = redirectUrl;

  constructor(@Inject(MAT_DIALOG_DATA) private data: any,
    public dialogRef: MatDialogRef<NoteDetailsComponent>,
    private dataCollectorService: DataCollectorService,
    private notesService: NotesService,
    private toast: HotToastService,
    private utilityService: UtilityService,
    private zuperDialogService: ZuperDialogService,
    private downloadFileService: DownloadFileLinkService,
    private _resolve: ComponentFactoryResolver,
    private _injector: Injector,
    private _appRef: ApplicationRef
  ) { }

  ngOnInit(): void {
    this.noteUid = this.data.data.noteUid;
    this.currentModule = this.data.data.module;
    this.showEditIcon = this.data.data.showEditIcon ?? false;
    this.getNoteDetails(this.noteUid);
  }

  getNoteDetails(note: any): void{
    this.dataCollectorService.getData(this.notesService.updateUrl(this.notesService.notesUrl,this.noteUid))
    .subscribe((response: any)=>{
        this.note = response.data;
        this.createLightGalleryCollection();
    }, (error: any)=>{
        
    });
  }

  hasValidProfilePicture(profilePicture: string): boolean {
    if (profilePicture) {
        return profilePicture.indexOf('profile_picture.jpg') === -1;
    }
    return false;
  }

  isPrivateNote(note: any): boolean {
    return note && note.is_private === true;
  }

  isCurrentUser(user: any): boolean {
    if (user) {
        return user.user_uid === this.user.user_uid;
    }
    return false;
  }

  loop = async (data: any, cb: any): Promise<any> => {
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let index: number = 0; index < data.length; index++) {
        await cb(data[index]);
    }
  };

  async createLightGalleryCollection(): Promise<void> {
    const note = this.note;
    if (note?.attachments?.length > 0) {
        await this.loop(note.attachments, async (attachment) => {
            const data = {
                created_by_first_name: note.created_by?.first_name,
                created_by_last_name: note.created_by?.last_name,
                created_at: note.created_at,
                attachment_name: attachment.attachment_name,
                size: attachment.attachment_size,
                url: attachment.attachment
            };
            const attachmentType: string = this.getNoteAttachmentType(attachment.attachment);
            if (note?.geo_cordinates) {
                data['geo_cordinates'] = note.geo_cordinates;
            }
            if (attachmentType === 'IMAGE') {
                const imageDetails: any = this.compileGalleryDetails(GalleryDetailsComponent, (c) => { c.instance.data = data; });
                await new Promise((resolve) => {
                    setTimeout(() => {
                        this.allImages.push({
                            'src': attachment.attachment,
                            'name': attachment.attachment_name,
                            'subHtml': imageDetails.outerHTML
                        });
                        resolve(true);
                    },);
                });
            } else if (attachmentType === 'VIDEO') {
                const videoDetails: any = this.compileGalleryDetails(GalleryDetailsComponent, (c) => { c.instance.data = data; });
                await new Promise((resolve) => {
                    setTimeout(() => {
                        this.allImages.push({
                            name: attachment.attachment_name,
                            downloadUrl: attachment.attachment,
                            mediaType: 'video',
                            video: {
                                'source': [{
                                    'src': attachment.attachment,
                                    'type': 'video/mp4'
                                }],
                                'attributes': {
                                    'preload': false,
                                    'controls': true
                                }
                            },
                            subHtml: videoDetails.outerHTML
                        });
                        resolve(true);
                    },);
                });
            }
        });
    }
    if (this.allImages.length > 0) {
        this.lightGallery.refresh(this.allImages);
        this.allImages.map((ele, i) => {
            if (ele?.src) {
                this.allImageMap[ele.src] = {};
                this.allImageMap[ele.src]['index'] = i;
                this.allImageMap[ele.src]['attachment_name'] = ele.name;
            } else if (ele?.video?.source?.[0]?.src) {
                this.allImageMap[ele?.video?.source?.[0]?.src] = {};
                this.allImageMap[ele?.video?.source?.[0]?.src]['index'] = i;
                this.allImageMap[ele?.video?.source?.[0]?.src]['attachment_name'] = ele.name;
            }
        });
    }
  }

  compileGalleryDetails(component: any, onAttach: any): any {
    const compFactory: ComponentFactory<Component> = this._resolve.resolveComponentFactory(component);
    const compRef = compFactory.create(this._injector);
    if (onAttach) { onAttach(compRef); }
    this._appRef.attachView(compRef.hostView);
    compRef.onDestroy(() => this._appRef.detachView(compRef.hostView));
    return compRef.location.nativeElement.children[0];
  }

  getCursorStyle(url: string): any {
      const attachmentType: string = this.getNoteAttachmentType(url);
      return {
          cursor: (attachmentType === 'IMAGE' || attachmentType === 'VIDEO') ? 'zoom-in' : 'pointer'
      };
  }
  lightOnInit = (detail: any): void => {
      this.lightGallery = detail.instance;
  };

  lgAppendSubHTML = (detail: AfterAppendSubHtmlDetail): void => {
    document.querySelector('#download-attachment').addEventListener('click', () => {
        const attachmentObject = {};
        attachmentObject['attachment'] = this.allImages[detail.index]?.['src'] || this.allImages[detail.index]?.['downloadUrl'];
        attachmentObject['attachment_name'] = this.allImages[detail.index]['name'];
        this.downloadNoteAttachment(attachmentObject);
    });
  };
  openGallery(attachments: any[], url: string): void {
      const attachmentType: string = this.getNoteAttachmentType(url);
      if (attachmentType === 'IMAGE' || attachmentType === 'VIDEO') {
          const galleryArray: any[] = [];
          let attachmentIndex: number = -1;
          const viewableAttachments: any[] = this.getViewableAttachments(attachments);
          viewableAttachments.forEach((attachment: any) => {
              const filteredValue: any = this.allImages.find((value: any) => (value.src === attachment.attachment || value.downloadUrl === attachment.attachment));
              if (filteredValue) {
                  galleryArray.push(filteredValue);
              }
          });
          galleryArray.forEach((value: any, index: number) => {
              if (value.src === url || value.downloadUrl === url) {
                  attachmentIndex = index;
                  return 0;
              }
          });
          if (attachmentIndex > -1) {
              this.lightGallery.refresh(galleryArray);
              this.lightGallery.openGallery(attachmentIndex);
          }
      }
  }

  getNoteAttachmentType(attachment: string): string {
    if (attachment) {
        const extension: string = attachment.split('.').pop();
        return this.utilityService.getAttachmentTypeEnum(extension);
    }
  }

  getViewableAttachments(attachments: any[]): any[] {
    const viewableAttachments: any[] = [];
    attachments.forEach((attachment: any) => {
        const attachmentType: string = this.getNoteAttachmentType(attachment?.attachment);
        if (attachmentType === 'IMAGE' || attachmentType === 'VIDEO') {
            viewableAttachments.push(attachment);
        }
    });
    return viewableAttachments;
  }

  getNonViewableAttachments(attachments: any[]): any[] {
    const nonViewableAttachments: any[] = [];
    attachments.forEach((attachment: any) => {
        const attachmentType: string = this.getNoteAttachmentType(attachment?.attachment);
        if (attachmentType !== 'IMAGE' && attachmentType !== 'VIDEO') {
            nonViewableAttachments.push(attachment);
        }
    });
    return nonViewableAttachments;
  }

  getPlaceholderOptions(attachment: any, showCloseIcon: boolean, showDownloadIcon: boolean): FilePlaceholderOptions {
    return {
        fileName: attachment.attachment_name ?? 'File',
        fileSize: attachment.attachment_size,
        fileType: attachment.attachment_type,
        fileLink: attachment.attachment,
        showCloseIcon: showCloseIcon,
        showDownloadIcon: showDownloadIcon
    };
  }

  fileEventEmitHandler(event: any, index?: number): void {
    if (event) {
        if (event.type === 'download') {
            this.downloadNoteAttachment(event.data);
        }
    }
  }

  downloadNoteAttachment(attachment: any): void {
    if (attachment.attachment) {
        const options: any = {
            params: {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                'file_url': attachment.attachment
            },
            responseType: 'arraybuffer',
            observe: 'response'
        };
        this.downloadFileService.downloadFileFromLink(attachment.attachment_name, options);
    } else {
        this.toast.error('Invalid file');
    }
  }
  getAssociatedModuleIcon(module: string): string {
    return ModuleIcon[module];
  }

  getModuleUid(module: string, associatedDetails: any): string {
        module = moduleKey[module] ?? module;
        if(!_.isEmpty(associatedDetails)){
            switch(module){
                case 'JOB':
                    return `${associatedDetails?.job_uid}`;
                case 'CUSTOMER':
                    return `${associatedDetails?.customer_uid}`;
                case 'ORGANIZATION':
                    return `${associatedDetails?.organization_uid}`;
                case 'PROPERTY':
                    return `${associatedDetails?.property_uid}`;
                case 'ESTIMATES':
                case 'ESTIMATE':
                    return `${associatedDetails?.estimate_uid}`;
                case 'INVOICE':
                    return `${associatedDetails?.invoice_uid}`;
                case 'ASSETS':
                    return `${associatedDetails?.asset_uid}`;
                case 'SERVICE_CONTRACT':
                case 'SERVICE_CONTRACTS':
                    return `${associatedDetails?.contract_uid}`;
                case 'PROJECT':
                    return `${associatedDetails?.project_uid}`;
            }
        }
  }

  getAssociatedModuleName(module: string, associatedDetails: any): string {
    module = moduleKey[module] ?? module;
    if(!_.isEmpty(associatedDetails)){
        switch(module){
            case 'JOB':
                return `${associatedDetails?.work_order_number?associatedDetails?.work_order_number+' - ':''}${associatedDetails?.job_title}`;
            case 'CUSTOMER':
                return `${associatedDetails?.customer_first_name} ${associatedDetails?.customer_last_name ?? ''}`;
            case 'ORGANIZATION':
                return `${associatedDetails?.organization_name}`;
            case 'PROPERTY':
                return `${associatedDetails?.property_name}`;
            case 'ESTIMATES':
            case 'ESTIMATE':
                return `${associatedDetails?.prefix?associatedDetails?.prefix+' - ':''}${associatedDetails?.estimate_no || ''}`;
            case 'INVOICE':
                return `${associatedDetails?.prefix?associatedDetails?.prefix+' - ':''}${associatedDetails?.invoice_no || ''}`;
            case 'ASSETS':
                return `${associatedDetails?.asset_code?associatedDetails?.asset_code+' - ':''}${associatedDetails?.asset_name ?? ''}`;
            case 'SERVICE_CONTRACT':
            case 'SERVICE_CONTRACTS':
                return `${associatedDetails.contract_number?associatedDetails.contract_number+' - ':''}${associatedDetails?.contract_name}`;
            case 'PROJECT':
                return `${associatedDetails?.project_number ? associatedDetails.project_number+' - ':''}${associatedDetails?.project_name}`;
        }
    }
}

  checkModuleIsAssociated(module?: any): boolean{
      if(this.note?.[ParamKey[module]]){
          return true;
      }
      else{
          return false;
      }
  }

  editNote(): void{
    this.dialogRef.close('true');
  }

  getNoteVisibility(note: any): NoteVisibilityType {
      return this.notesService.getNoteVisibility(note);
  }
}
